'use client'

import React, { useState } from "react";
import InputField from "@/app/components/input";
import CalendarInput from "../../calendar-input";
import CalendarIcon from "../../svg/calendar";
import Button from "../../button";
import ArrowRight from "../../svg/arrow-right";
import Pin from "../../svg/pin";
import Square from "../../svg/square";
import { CheckboxButton } from "../../checkbox-input";

interface Step1Props {
    onNext: (data: any) => void;
    initialData: any;
    lang?: string; 
    id?: string;
}

const Step1: React.FC<Step1Props> = ({ onNext, initialData, lang, id }) => {
    const [data, setData] = useState({
        datefrom: initialData.datefrom || null,
        dateto: initialData.dateto || null,
        option1: initialData.option1 || "",
        option2: initialData.option2 || "",
        option3: initialData.option3 || "",
        option4: initialData.option4 || "",
        option5: initialData.option5 || "",
    });

    const [errors, setErrors] = useState({
        datefrom: false,
        dateto: false,
    });

    const [checkboxes, setCheckboxes] = useState<{ [key: string]: boolean }>({
        option1: false,
        option2: false,
        option3: false,
        option4: false,
        option5: false,
    });

    const [checkboxError, setCheckboxError] = useState(false);


    const handleNext = () => {
        const newErrors = {
            datefrom: !data.datefrom,
            dateto: !data.dateto,
        };
    
        const isAnyOptionSelected = Object.values(checkboxes).some(Boolean);
        setErrors(newErrors);
        setCheckboxError(!isAnyOptionSelected);
    
        if (!Object.values(newErrors).some((error) => error) && isAnyOptionSelected) {
            onNext({ ...data, checkboxes });
        }
    };

    const handleChange = (id: string) => {
        setCheckboxes((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleCheckboxChange = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
        setCheckboxes((prev) => ({ ...prev, [id]: e.target.checked }));
        setData((prev) => ({ ...prev, [id]: e.target.checked ? e.target.value : "" }));
    };
    
    const requredText = lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt';

    return (
        <div id={id} className="rounded-[40px] p-[48px] shadow-[0_10px_24px_0_#00000026] max-[576px]:p-[20px] max-[576px]:rounded-[20px] max-[576px]:p-[20px]">
            <h2 className="text-[32px] font-bold text-violet-500 mb-[40px] max-[576px]:text-[20px] max-[576px]:leading-[24px] max-[576px]:mb-[24px] ">
                {lang === 'en' ? 'Please fill in the required information' : 'Vänligen fyll i nödvändig information'}
            </h2>
            <div className="grid grid-cols-3 gap-6 mb-6 max-[992px]:grid-cols-1">
                <CheckboxButton
                    id="option1"
                    topText= {lang === 'en' ? 'small furniture' : 'små möbler'}
                    label="< 8 m³"
                    checked={checkboxes.option1}
                    onChange={(e) => handleCheckboxChange("option1", e)}
                    value="< 8 m³"
                    hasError={checkboxError}
                />

                <CheckboxButton
                    id="option2"
                    topText={lang === 'en' ? 'one room apartment' : 'enrumslägenhet'}
                    label="16 - 24 m³"
                    checked={checkboxes.option2}
                    onChange={(e) => handleCheckboxChange("option2", e)}
                    value="16 - 24 m³"
                    hasError={checkboxError}
                />
                <CheckboxButton
                    id="option3"
                    topText={lang === 'en' ? 'one - two room apartment' : 'en - två rumslägenhet'}
                    label="24 - 32 m³"
                    checked={checkboxes.option3}
                    onChange={(e) => handleCheckboxChange("option3", e)}
                    value="24 - 32 m³"
                    hasError={checkboxError}
                />
            </div>
            <div className="grid grid-cols-2 gap-6 mb-6 max-[992px]:grid-cols-1">
                <CheckboxButton
                    id="option4"
                    topText={lang === 'en' ? 'two - three bedroom apartment' : 'två - tre sovrum'}
                    label="32 - 40 m³"
                    checked={checkboxes.option4}
                    onChange={(e) => handleCheckboxChange("option4", e)}
                    value="32 - 40 m³"
                    hasError={checkboxError}
                />

                <CheckboxButton
                    id="option5"
                    topText={lang === 'en' ? 'three bedroom house or larger' : 'hus med tre sovrum eller större'}
                    label="> 40 m³"
                    checked={checkboxes.option5}
                    onChange={(e) => handleCheckboxChange("option5", e)}
                    value="> 40 m³"
                    hasError={checkboxError}
                />
            </div>
            <div className="space-y-[32px]">
                <div className="grid grid-cols-2 gap-6 max-[992px]:grid-cols-1">
                    <div className="space-y-6">
                        <h3 className="font-medium text-sm text-violet-500 leading-[143%] uppercase mb-[8px] max-[576px]:text-[12px] max-[576px]:leading-[12px]">
                            {lang === 'en' ? 'please select desired warehousing time' : 'vänligen välj önskad lagertid'}
                        </h3>
                        <CalendarInput
                            label={lang === 'en' ? 'from' : 'från'}
                            value={data.dateto}
                            onChange={(dateto) => setData({ ...data, dateto })}
                            icon={<CalendarIcon />}
                            placeholder="YYYY/MM/DD"
                            errorMessage={errors.dateto ? requredText : ""}
                        />
                    </div>
                    <div className="space-y-6 mt-[45px] max-[992px]:mt-[0]">
                        <CalendarInput
                            label={lang === 'en' ? 'to' : 'till'}
                            value={data.datefrom}
                            onChange={(datefrom) => setData({ ...data, datefrom })}
                            icon={<CalendarIcon />}
                            placeholder="YYYY/MM/DD"
                            errorMessage={errors.datefrom ? requredText : ""}
                        />
                    </div>
                </div>
            </div>
            <div className="flex justify-end mt-[40px]">
                <Button
                    onClick={handleNext}
                    mobileFull="yes"
                    text={lang === 'en' ? 'continue' : 'fortsätta' }
                    icon={<ArrowRight />}
                    type="primary"
                    iconPostiton="right"
                    size="big"
                />
            </div>
        </div>
    );
};

export default Step1;
